import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useActionsQuery } from '../../hooks/useActionsQuery';
import { exportUserLogs } from '../../services/userLogs';
import { exportModeratorLogs } from '../../services/moderatorLogs';
import useExportData from '../../hooks/useExportData';
import useDebounce from '../../hooks/useDebounce';
import { getUrlForOpenEntity } from '../../hooks/useEntityQuery';
import { UserActionResponse, LogsTabsType } from '../../types/api/logs';
import useCleanPaginationURL from '../../hooks/useCleanPaginationURL';
import { usePaginationTextReplace } from '../../hooks/usePaginationTextReplace';
import RegularPageLayout from '../../layouts/RegularPageLayout';
import { toUTCEndOfDayWithTime, toUTCWithTime } from '../../helpers/dates';
import LogsFilterFormModal from './components/LogsFilterFormModal';
import Segment from '../navigation/Segment/Segment';
import { logsTabsData } from '../navigation/Segment/Segment+Data';
import { Placeholder, createErrorPlaceholder } from '../basic';
import { useAlert } from '../../context/alert';
import { pageSize } from '../../services/settings';
import { useSorting } from "../../hooks/useSorting";
import { createDataTableFilterColumns } from "../common/TableComponent";
import { logsFilterConfig, LogFilters } from "./LogsList+Filter";
import { ActionType, EntityType, ActionStatus, LogOrderingField, SortDirection, UserActionsRequest } from "../../types/api/logs";
import { logsTableConfig } from './LogsList+Table';
import { TextInput, Stack, Button, ActionMenu, ActionList } from '@primer/react'
import { Table, DataTable } from '@primer/react/experimental'
import { SearchIcon, DownloadIcon, XCircleIcon, XCircleFillIcon, XIcon } from '@primer/octicons-react'


export default function LogsList() {

    const navigate = useNavigate();

    const [pageIndex, setPageIndex] = React.useState(1)
    const [searchParams] = useSearchParams();
    const rawtabsType = searchParams.get('type');
    const tabsType = useMemo(() => rawtabsType || undefined, [rawtabsType]);
    const [currentRouteEntityId, setCurrentRouteEntityId] = useState<string | undefined>(undefined);

    const [searchQuery, setSearchQuery] = useState<string>('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500)


    const { isLoading: isLoadingExport, exportAction } = useExportData();
    const { setSuccess, setError } = useAlert();

    const [filters, setFilters] = useState<LogFilters>({});
    const [activeFilter, setActiveFilter] = useState<string | null>(null);
    const [userId, setUserId] = useState('');
    const actor = useDebounce(userId.trim(), 500);
    const actorInput = useRef<HTMLInputElement>(null);
    const actorValid = !actor || /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(actor);
    const { sortState, handleSort, getSortIcon } = useSorting<LogOrderingField>('created_at', 'desc');
    // Shared createOrderingParam reverses directions; keep the correct API contract local to logs.
    const ordering = `${sortState.direction === 'desc' ? '-' : ''}${sortState.field}` as SortDirection;

    // Filters Modal
    const [filterOpened, setFilterOpened] = useState<boolean>(false);
    const [dateFilterOpened, setDateFilterOpened] = useState<boolean>(false);

    // Filter request params
    const [fromDate, setFromDate] = useState<string>('');
    const [toDate, setToDate] = useState<string>('');

    const [fromTime, setFromTime] = useState<string>('');
    const [toTime, setToTime] = useState<string>('');
    // Filter draft params for modal
    const [fromDateDraft, setFromDateDraft] = useState<string>('');
    const [toDateDraft, setToDateDraft] = useState<string>('');

    const [fromTimeDraft, setFromTimeDraft] = useState<string>('');
    const [toTimeDraft, setToTimeDraft] = useState<string>('');

    // Prepare filter request params
    const createdFromIsoDate = fromDate ? toUTCWithTime(fromDate, fromTime || undefined) : undefined;
    const createdToIsoDate = toDate ? toUTCEndOfDayWithTime(toDate, toTime || undefined) : undefined;

    useEffect(() => {
        setPageIndex(1);
    }, [debouncedSearchQuery]);

    const isUserTab = () => {
        const type = tabsType as LogsTabsType
        return type !== 'moderators'
    }

    const requestFilters: UserActionsRequest = {
        search: debouncedSearchQuery || undefined, user_id: actor || undefined, ordering,
        action_type: filters.logActionType as ActionType | undefined,
        entity_type: filters.logEntityType as EntityType | undefined,
        status: filters.logStatus as ActionStatus | undefined,
        created_from: createdFromIsoDate, created_to: createdToIsoDate,
    };
    useEffect(() => { setPageIndex(1); }, [tabsType, actor, filters, ordering, createdFromIsoDate, createdToIsoDate]);
    useEffect(() => { setFilters(current => ({ ...current, logActionType: undefined })); }, [tabsType]);

    const {
        isLoading,
        data,
        isFetched,
        isError
    } = useActionsQuery(
        isUserTab(),
        debouncedSearchQuery,
        pageIndex,
        pageSize,
        requestFilters.action_type,
        requestFilters.entity_type,
        requestFilters.status,
        ordering,
        requestFilters.user_id,
        undefined, // entityId,
        createdFromIsoDate, // createdFrom,
        createdToIsoDate, // createdTo,
        undefined, //appScreen,
        undefined, //clientVersion,
        { enabled: actorValid },
    );

    useCleanPaginationURL();
    usePaginationTextReplace()

    // EXPORT LOGS
    const downloadLogs = (format: string) => {
        if (isUserTab()) {
            downloadUserLogs(format)
        } else {
            downloadModeratorsLogs(format)
        }
    }

    const downloadUserLogs = async (format: string) => {
        try {
            const result = await exportAction(
                () => exportUserLogs(format, requestFilters),
                "logs",
                format
            );
            if (result.success) {
                setSuccess('Загрузка логов успешно началась');
            }
        } catch (err) { }
    }

    const downloadModeratorsLogs = async (format: string) => {
        try {
            const result = await exportAction(
                () => exportModeratorLogs(format, requestFilters),
                "logs",
                format
            );
            if (result.success) {
                setSuccess('Загрузка логов успешно началась');
            }
        } catch (err) { }
    }

    const clearSearchInput = () => {
        setSearchQuery("")
    }

    const openLogActionEntity = async (action: UserActionResponse) => {
        if (!action.entityId) return;
        setCurrentRouteEntityId(action.id)
        try {
            const url = await getUrlForOpenEntity(action.entityType, action.entityId)
            if (url) {
                //window.open(url, '_blank')
                navigate(url)
            }
        } catch (error) {
            console.error('Error opening entity:', error)
            setError('Не удается получить объект действия')
        } finally {
            setCurrentRouteEntityId(undefined)
        }
    }

    // FILTER MODAL
    const handleDismiss = () => {
        setFromDate('');
        setFromDateDraft('');

        setToDate('');
        setToDateDraft('');

        setToTime('');
        setToTimeDraft('');

        setFromTime('');
        setFromTimeDraft('');

        setFilterOpened(false);
    }
    const handleApply = () => {
        setFromDate(fromDateDraft);
        setToDate(toDateDraft);
        setToTime(toTimeDraft);
        setFromTime(fromTimeDraft);

        setFilterOpened(false);
    }

    const tableConfig = logsTableConfig({
        users: isUserTab(), openingId: currentRouteEntityId, openEntity: openLogActionEntity,
        openUser: id => navigate(isUserTab() ? `/users/user?id=${id}` : '/moderators'),
    });
    const columns = createDataTableFilterColumns(tableConfig, key => {
        if (key === 'userId') actorInput.current?.focus();
        else setActiveFilter(key);
    }, key => key === 'userId' ? !!actor : !!filters[key as keyof LogFilters]).map(column =>
        column.id === 'created_at' ? { ...column, header: () => <button onClick={() => handleSort('created_at')}>
            {getSortIcon('created_at')} Дата и время (UTC)
        </button> } : column,
    );

    const emptyListDescription = `По вашему запросу не найдено
    подходящих логов`;
    const emptySearchDescription = `ПВ сервисе не создано
    ни одного лога`;

    return (
        <RegularPageLayout
            header={
                <Stack justify="space-between" direction="horizontal">
                    <Segment items={logsTabsData} />
                    <Stack direction="horizontal">
                        <TextInput
                            leadingVisual={SearchIcon}
                            trailingAction={
                                searchQuery.length > 0 ? (
                                    <TextInput.Action
                                        onClick={() => {
                                            setSearchQuery('');
                                        }}
                                        icon={XCircleFillIcon}
                                        aria-label="Очистить"
                                    />
                                ) : undefined
                            }
                            value={searchQuery}
                            placeholder='Поиск'
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                            }}
                        />

                        <LogsFilterFormModal
                            filterOpened={filterOpened}
                            setFilterOpened={setFilterOpened}

                            dateFilterOpened={dateFilterOpened}
                            setDateFilterOpened={setDateFilterOpened}

                            fromDate={fromDateDraft}
                            setFromDate={setFromDateDraft}
                            toDate={toDateDraft}
                            setToDate={setToDateDraft}

                            fromTime={fromTimeDraft}
                            setFromTime={setFromTimeDraft}
                            toTime={toTimeDraft}
                            setToTime={setToTimeDraft}

                            onDismiss={handleDismiss}
                            onApply={handleApply}
                        />

                        <ActionMenu>
                            <ActionMenu.Anchor>
                                <Button
                                    variant="default"
                                    size="small"
                                    loading={isLoadingExport}
                                    disabled={!actorValid}
                                    leadingVisual={DownloadIcon}
                                >
                                    Экспортировать логи
                                </Button>
                            </ActionMenu.Anchor>
                            <ActionMenu.Overlay>
                                <ActionList >
                                    <ActionList.Item
                                        key="json-format"
                                        onSelect={() => downloadLogs('json')}
                                    >
                                        <Stack align="center" direction="horizontal" gap='none'>
                                            JSON
                                        </Stack>
                                    </ActionList.Item>
                                    <ActionList.Item
                                        key="csv-format"
                                        onSelect={() => downloadLogs('csv')}
                                    >
                                        <Stack align="center" direction="horizontal" gap='none'>
                                            CSV
                                        </Stack>
                                    </ActionList.Item>
                                </ActionList>
                            </ActionMenu.Overlay>
                        </ActionMenu>
                    </Stack>
                </Stack>
            }
            content={
                <Stack>

                    <Stack direction="horizontal" className="flex-wrap">
                        {logsFilterConfig(isUserTab()).map(filter => <ActionMenu key={filter.key}
                            open={activeFilter === filter.key} onOpenChange={open => setActiveFilter(open ? filter.key : null)}>
                            <ActionMenu.Button>{filter.label}: {filter.options.find(option => option.value === filters[filter.key as keyof LogFilters])?.label ?? 'Все'}</ActionMenu.Button>
                            <ActionMenu.Overlay className="max-h-[420px] overflow-y-auto">
                                <ActionList selectionVariant="single" aria-label={filter.label}>
                                    {[{ value: '', label: 'Все' }, ...filter.options].map(option => <ActionList.Item key={option.value}
                                        selected={(filters[filter.key as keyof LogFilters] || '') === option.value}
                                        onSelect={() => { setFilters(current => ({ ...current, [filter.key]: option.value || undefined })); setActiveFilter(null); }}>
                                        {option.label}
                                    </ActionList.Item>)}
                                </ActionList>
                            </ActionMenu.Overlay>
                        </ActionMenu>)}
                        <TextInput ref={actorInput} value={userId} placeholder="UUID пользователя" aria-label="UUID пользователя"
                            onChange={event => setUserId(event.target.value)} validationStatus={actorValid ? undefined : 'error'} />
                        <Button onClick={() => { setFilters({}); setUserId(''); setSearchQuery(''); handleDismiss(); }}>Сбросить фильтры</Button>
                    </Stack>
                    {!actorValid && <span role="alert">Введите UUID пользователя</span>}
                    {(fromDate || toDate) && <span>Период (UTC): {fromDate} {fromTime} - {toDate} {toTime}</span>}
                    {isError && !isLoading && createErrorPlaceholder()}
                    {isLoading && <Table.Container>
                        <Table.Skeleton
                            aria-label="Загрузка журнала"
                            rows={pageSize}
                            columns={tableConfig.map((el) => {
                                return {
                                    header: el.title,
                                    id: el.id,
                                }
                            })}
                        />
                    </Table.Container>}
                    {isFetched && !isError && data?.list.length === 0 && (
                        <Placeholder
                            icon={<XCircleIcon size={24} className='opacity-40' />}
                            header={searchQuery.length > 0 ? 'Ничего не было найдено' : 'Логи не найдены'}
                            description={searchQuery.length > 0 ? emptySearchDescription : emptyListDescription}
                            button={searchQuery.length > 0 && <Button
                                variant="default"
                                leadingVisual={XIcon}
                                onClick={clearSearchInput}>
                                Очистить</Button>}
                        />
                    )}
                    {isFetched && data && data.list.length > 0 &&
                        <Table.Container>
                            <DataTable
                                aria-label="Журнал действий"
                                data={data.list}
                                columns={columns}
                            />
                        </Table.Container>
                    }
                    {isFetched && data && data.list.length > 0 &&
                        (<div className="sticky bottom-0 bg-white border-t border-gray-200 z-10 -mt-4">
                            <Table.Pagination key={`${tabsType}-${pageIndex}`} aria-label="pagination" pageSize={pageSize} totalCount={Number(data.totalCount)} onChange={({
                                pageIndex
                            }) => {
                                setPageIndex(pageIndex + 1);
                            }} defaultPageIndex={pageIndex - 1} />
                        </div>
                        )}
                </Stack>
            }
        ></RegularPageLayout>

    )
}
